import React, { useState } from 'react';

const shadeParts = (name) => {
  const parts = String(name || '').split(/\s+[—–-]\s+/).map((part) => part.trim()).filter(Boolean);
  if (parts.length >= 3) return { title: `${parts[0]} · ${parts[1]}`, detail: parts.slice(2).join(' · ') };
  if (parts.length === 2) return { title: parts[0], detail: parts[1] };
  return { title: String(name || ''), detail: '' };
};

const swatchStyle = ({ hex, imageUrl }) => ({
  backgroundColor: hex || '#F4F1EC',
});

const Swatch = ({ shade, className }) => {
  const hasColour = !!(shade.hex || shade.imageUrl);
  return (
    <div
      className={`chat-shade-swatch flex-shrink-0 flex items-center justify-center rounded-xl ring-offset-2 ${shade.isBest ? 'ring-2 ring-[#2A324B]' : 'ring-1 ring-[#2A324B]/15'} ${className}`}
      style={swatchStyle(shade)}
      role="img"
      aria-label={hasColour ? `${shade.name} swatch` : `${shade.name} colour preview unavailable`}
    >
      {shade.imageUrl && <img src={shade.imageUrl} alt="" aria-hidden="true" className="w-full h-full object-cover rounded-xl" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}
      {!hasColour && <span className="px-1 text-center text-[10px] leading-tight text-[#2A324B]/60">No colour</span>}
    </div>
  );
};

// Keep swatches similarly sized across one, two, and three options. Long
// catalogue names switch to readable rows without surrounding shade tiles.
export default function ShadeMatchCard({ productName, brand, heroUrl, shades = [] }) {
  const [failedHero, setFailedHero] = useState('');
  const hasHero = !!heroUrl && heroUrl !== failedHero;
  const longNames = shades.some((shade) => shade.name.length > 24);
  const expanded = shades.length < 3;

  return (
    <div className="chat-shade-trio" data-product-image={hasHero ? 'hero' : 'none'}>
      <div className="flex gap-3 items-start">
        {hasHero && (
          <div className="w-[64px] h-[64px] rounded-xl overflow-hidden flex-shrink-0 bg-[#F2EAE1]/50 ring-1 ring-[#2A324B]/8">
            <img src={heroUrl} alt={productName} className="w-full h-full object-cover object-center" loading="lazy" onError={() => setFailedHero(heroUrl)} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <p className="chat-shade-product-name text-[15px] font-semibold text-[#2A1212] leading-tight">{productName}</p>
          {brand && <p className="text-[13px] uppercase tracking-wide text-[#2A324B]/55 mt-0.5">{brand}</p>}
        </div>
      </div>

      {shades.length > 0 && (longNames ? (
        <div className="mt-4 divide-y divide-[#2A324B]/10" data-shade-layout="long">
          {shades.map((shade, index) => {
            const parts = shadeParts(shade.name);
            return (
              <div key={`${shade.name}-${index}`} className="flex items-center gap-3 min-w-0 py-2.5 first:pt-0 last:pb-0">
                <Swatch shade={shade} className={hasHero ? 'w-[56px] h-[48px]' : 'w-[72px] h-[64px]'} />
                <div className="min-w-0 flex-1">
                  <p className={`text-[12px] font-semibold leading-tight ${shade.isBest ? 'text-[#2A324B]' : 'text-[#2A324B]/60'}`}>{shade.isBest ? 'Best match' : 'Alternative'}</p>
                  <p className="mt-0.5 text-[13px] font-semibold text-[#2A1212] leading-snug break-words">{parts.title}</p>
                  {parts.detail && <p className="mt-0.5 text-[12px] text-[#2A324B]/65 leading-snug break-words">{parts.detail}</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="mt-4 flex justify-center gap-3" data-shade-layout={expanded ? 'expanded' : 'compact'}>
          {shades.map((shade, index) => (
            <div
              key={`${shade.name}-${index}`}
              className="min-w-0 flex-1 max-w-[96px] flex flex-col text-center"
            >
              <Swatch shade={shade} className={`w-full mx-auto ${hasHero ? 'h-[56px]' : 'h-[76px]'}`} />
              <p className="mt-2 text-[13px] font-semibold text-[#2A1212] leading-snug break-words">{shade.name}</p>
              <p className={`mt-auto pt-1 text-[12px] leading-tight ${shade.isBest ? 'font-semibold text-[#2A324B]' : 'text-[#2A324B]/55'}`}>{shade.isBest ? 'Best match' : 'Alternative'}</p>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
